import { MouseEvent, useEffect, useState } from 'react'
import { Link } from 'gatsby'
import { pushAnalyticsEvent } from '../../utils/googleAnalytics'
import { Logo } from '../Logo'
import LinkButton from './LinkButton'
import {
  compareDropDownContent,
  platformDropDownContent,
  resourcesDropDownContent,
  solutionsDropDownContent,
} from '../common/nav/data'
import ResourcedDropdown from './nav/ResourcesDropdown'
import { useWindowScroll } from 'react-use'

enum DropDownMenu {
  PLATFORM = 'PLATFORM',
  SOLUTIONS = 'SOLUTIONS',
  RESOURCES = 'RESOURCES',
  COMPARE = 'COMPARE',
}

const Nav = () => {
  const [showMobile, setShowMobile] = useState(false)
  const [menuHovered, setMenuHovered] = useState<DropDownMenu | undefined>()
  const [scrolled, setScrolled] = useState(false)
  const { y } = useWindowScroll()

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (y > 50) {
        setScrolled(() => true)
      } else {
        setScrolled(() => false)
      }
    }
  }, [y])

  const toggleButton = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault()
    setMenuHovered(undefined)
    setShowMobile(!showMobile)
  }

  const getNavItemClassName = (menuType: DropDownMenu) => {
    return menuHovered === menuType
      ? `nav-item-hover ${scrolled ? 'nav-item-hover-scrolled' : ''}`
      : ''
  }

  const dropdownClassName = `nav-dropdown-menu ${
    scrolled ? 'nav-dropdown-menu-scrolled' : ''
  }`

  const dropdownMenu = () => {
    if (!menuHovered) {
      return
    }

    if (menuHovered === DropDownMenu.RESOURCES) {
      return (
        <div
          className={dropdownClassName}
          onMouseLeave={() => setMenuHovered(undefined)}
        >
          <ResourcedDropdown content={resourcesDropDownContent} />
        </div>
      )
    }

    if (menuHovered === DropDownMenu.COMPARE) {
      return (
        <div
          className={`compare ${dropdownClassName}`}
          onMouseLeave={() => setMenuHovered(undefined)}
        >
          <div className="row nav-dropdown-menu-content">
            {compareDropDownContent.map((item, index) => (
              <div
                key={`${item.to}-${index}`}
                className="col-xs-12 col-sm-4 dropdown-menu-item"
              >
                <Link to={item.to}>
                  <h3 className="nav-dropdown-item-title">{item.title}</h3>
                </Link>
              </div>
            ))}
          </div>
        </div>
      )
    }

    const content =
      menuHovered === DropDownMenu.PLATFORM
        ? platformDropDownContent
        : solutionsDropDownContent

    return (
      <div
        className={dropdownClassName}
        onMouseLeave={() => setMenuHovered(undefined)}
      >
        <div className="row nav-dropdown-menu-content">
          {content.map((item, index) => (
            <div
              key={`${item.to}-${index}`}
              className="col-xs-12 col-sm-4 nav-dropdown-menu-item"
            >
              <div>
                <Link to={item.to}>
                  <h3 className="nav-dropdown-item-title">{item.title}</h3>
                </Link>
              </div>
              <div>
                <p>{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <nav className={`nav ${scrolled ? 'scrolled' : ''}`}>
      <ul className="row middle-xs fluid-margin-container nav-content">
        <li className="nav-logo" onMouseEnter={() => setMenuHovered(undefined)}>
          <Link to="/" title="Home">
            <Logo />
          </Link>
        </li>

        <div className="nav-menu">
          <li
            onMouseEnter={() => setMenuHovered(DropDownMenu.PLATFORM)}
            className={getNavItemClassName(DropDownMenu.PLATFORM)}
          >
            <Link
              to="/features"
              title="Platform"
              activeClassName="nav-item-active"
            >
              Platform
            </Link>
          </li>

          <li
            onMouseEnter={() => setMenuHovered(DropDownMenu.SOLUTIONS)}
            className={getNavItemClassName(DropDownMenu.SOLUTIONS)}
          >
            <Link to="/" title="Solutions">
              Solutions
            </Link>
          </li>

          <li onMouseEnter={() => setMenuHovered(undefined)}>
            <Link to="/demo" title="Demo" activeClassName="nav-item-active">
              Demo
            </Link>
          </li>

          <li onMouseEnter={() => setMenuHovered(undefined)}>
            <Link
              to="/pricing"
              title="Pricing"
              activeClassName="nav-item-active"
            >
              Pricing
            </Link>
          </li>

          <li
            onMouseEnter={() => setMenuHovered(DropDownMenu.RESOURCES)}
            className={getNavItemClassName(DropDownMenu.RESOURCES)}
          >
            <Link to="/" title="Resources">
              Resources
            </Link>
          </li>

          <li
            onMouseEnter={() => setMenuHovered(DropDownMenu.COMPARE)}
            className={getNavItemClassName(DropDownMenu.COMPARE)}
          >
            <Link to="/" title="Compare">
              Compare
            </Link>
          </li>

          <li onMouseEnter={() => setMenuHovered(undefined)}>
            <Link to="/about" title="About" activeClassName="nav-item-active">
              About
            </Link>
          </li>

          <li onMouseEnter={() => setMenuHovered(undefined)}>
            <a href={process.env.BLOG_URL as string} title="Blog">
              Blog
            </a>
          </li>

          <li className="nav-mobilemenu">
            <button className="nav-mobilemenu-button" onClick={toggleButton}>
              Menu
            </button>
          </li>

          <li
            className="nav-signin"
            onMouseEnter={() => setMenuHovered(undefined)}
          >
            <a
              href={process.env.LOGIN_URL}
              className="secondary"
              onClick={() => pushAnalyticsEvent('Button', 'Click-Signin')}
              title="Sign In"
            >
              Sign In
            </a>
          </li>

          {scrolled && (
            <li className="try-scrolled">
              <LinkButton url="/free-trial" label="Try it out" prefetch />
            </li>
          )}
        </div>
      </ul>

      {showMobile && (
        <div className="nav_mobile">
          <Link
            to="/features"
            activeClassName="nav-item-active"
            onMouseEnter={() => setMenuHovered(DropDownMenu.PLATFORM)}
          >
            Platform
          </Link>
          <Link
            to="/"
            onMouseEnter={() => setMenuHovered(DropDownMenu.SOLUTIONS)}
          >
            Solutions
          </Link>
          <Link
            to="/demo"
            activeClassName="nav-item-active"
            onMouseEnter={() => setMenuHovered(undefined)}
          >
            Demo
          </Link>
          <Link
            to="/pricing"
            activeClassName="nav-item-active"
            onMouseEnter={() => setMenuHovered(undefined)}
          >
            Pricing
          </Link>
          <Link
            to="/"
            onMouseEnter={() => setMenuHovered(DropDownMenu.RESOURCES)}
          >
            Resources
          </Link>
          <Link
            to="/"
            onMouseEnter={() => setMenuHovered(DropDownMenu.COMPARE)}
          >
            Compare
          </Link>
          <Link
            to="/about"
            activeClassName="nav-item-active"
            onMouseEnter={() => setMenuHovered(undefined)}
          >
            About
          </Link>
          <a
            href={process.env.BLOG_URL}
            onMouseEnter={() => setMenuHovered(undefined)}
          >
            Blog
          </a>
          <LinkButton url="/free-trial" label="Try it out" mobile prefetch />
          <a
            href={process.env.LOGIN_URL}
            className="secondary"
            onClick={() => pushAnalyticsEvent('Button', 'Click-Signin')}
            onMouseEnter={() => setMenuHovered(undefined)}
          >
            Sign In
          </a>
        </div>
      )}

      {dropdownMenu()}
    </nav>
  )
}

export default Nav
